/**
 * Artifact renderer registry — the single "pass the type in" entry point.
 * Artifacts are structured JSON now: the panel calls `resolveArtifact(json, name)`
 * with the parsed `structured_content` (or parsed `content` for a live stream).
 * The first renderer whose `parse` succeeds wins (name hint tried first, then
 * structural fallback). Returns null for anything unrecognised, so the panel
 * falls back to plain-markdown rendering of the artifact's `content`.
 */

import type { ArtifactRenderer, ResolvedArtifact } from './types';
import { jtbdRenderer } from './jtbd';
import { personaRenderer } from './persona';
import { fiveWhysRenderer } from './fiveWhys';
import { hmwRenderer } from './hmw';
import { roiRenderer } from './roiCheck';
import { competitorsRenderer } from './competitorsAssessment';
import { fiveQuestionScreenRenderer } from './fiveQuestionScreen';
import { marketResearchRenderer } from './marketResearch';

export const RENDERERS: ArtifactRenderer<any>[] = [
  jtbdRenderer,
  personaRenderer,
  fiveWhysRenderer,
  hmwRenderer,
  // Atlas analytical artifacts (ROI, competitors, 5-question screen, market research)
  roiRenderer,
  competitorsRenderer,
  fiveQuestionScreenRenderer,
  marketResearchRenderer,
];

/**
 * Resolve a parsed structured-JSON payload to its renderer + typed model.
 * `json` is the object form of `structured_content`.
 */
export function resolveArtifact(
  json: Record<string, unknown> | null | undefined,
  name?: string
): ResolvedArtifact | null {
  if (!json || typeof json !== 'object') return null;

  // 1) Prefer a renderer whose matchNames includes the artifact name — but still
  //    require its parse to succeed (a mislabelled payload falls through).
  const lname = (name ?? '').trim().toLowerCase();
  if (lname) {
    const named = RENDERERS.find((r) => r.matchNames.includes(lname));
    if (named) {
      const model = named.parse(json);
      if (model != null) return { renderer: named, model };
    }
  }

  // 2) Structural fallback — first successful parse wins.
  for (const renderer of RENDERERS) {
    const model = renderer.parse(json);
    if (model != null) return { renderer, model };
  }
  return null;
}
